import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  IconButton,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Collapse,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Close,
  AttachMoney,
  Schedule,
  CheckCircle,
  Info,
  Add,
  Delete,
  DirectionsCar,
  Receipt,
  ExpandMore,
  Person,
  Email,
  Payment,
  BrandingWatermark,
} from "@mui/icons-material";
import apiClient from "../../../utils/axios";
import { enqueueSnackbar } from "notistack";

const PaymentDialog = ({ open, onClose, vehicle, loadVehicles, onPaymentSuccess }) => {
  const [config, setConfig] = useState({
    precioPorHora: 1,
    typePriceCalc: "hourly",
    fixedPrice: 0,
    lostTicketValue: 0,
    invoiceDiscountHours: 1,
    tiempoGratisHabilitado: false,
    minutosGratis: 0,
    moneda: "USD",
    minutosSalida: 15,
    totalEspacios: 0,
    espacioParqueoUsado: 0,
  });
  const [cobroInfo, setCobroInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [procesandoPago, setProcesandoPago] = useState(false);
  const [facturas, setFacturas] = useState([]);
  const [nuevaFactura, setNuevaFactura] = useState({
    numero: "",
    valor: "",
  });
  const [facturasExpanded, setFacturasExpanded] = useState(false);
  const [comprobanteExpanded, setComprobanteExpanded] = useState(false);
  const [requiereComprobante, setRequiereComprobante] = useState(true);
  const [ticketPerdido, setTicketPerdido] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(null);
  const [montoPersonalizado, setMontoPersonalizado] = useState('');
  const [categoriaExpanded, setCategoriaExpanded] = useState(false);

  // ── Tipo de vehículo (corrección manual si la cámara falló) ─────────────
  const [categoriasVehiculo, setCategoriasVehiculo] = useState([]);
  const [tipoVehiculoActual, setTipoVehiculoActual] = useState(null);
  const [cambiandoTipoVehiculo, setCambiandoTipoVehiculo] = useState(false);
  const [recalcTick, setRecalcTick] = useState(0);

  // ── Horario de misa vigente ──────────────────────────────────────────────
  const [horarioMisa, setHorarioMisa] = useState(null);

  // ── Plan por cédula / placa ──────────────────────────────────────────────
  const [cedulaPlan, setCedulaPlan] = useState("");
  const [placaPlan, setPlacaPlan] = useState("");
  const [infoPlan, setInfoPlan] = useState(null);   // resultado de verificar_plan
  const [buscandoPlan, setBuscandoPlan] = useState(false);
  const [planExpanded, setPlanExpanded] = useState(false);
  // ── Tarjeta prepago ──────────────────────────────────────────────────────
  const [tarjetaInfo, setTarjetaInfo] = useState(null);  // { id, horas_totales, horas_usadas, activo }
  const [usarTarjetaPrepago, setUsarTarjetaPrepago] = useState(false);
  const [datosComprobante, setDatosComprobante] = useState({
    nombre: "",
    tipo_identificacion: "cedula",
    identificacion: "",
    correo: "",
    metodo: "efectivo",
    descripcion: "Pago Parqueadero",
  });

  const validarCedulaEcuatoriana = (cedula) => {
    return /^\d{10}$/.test(cedula);
  };

  const validarRuc = (ruc) => {
    return /^\d{13}$/.test(ruc);
  };

  const validarPasaporte = (pasaporte) => {
    return /^[A-Za-z0-9]{6,20}$/.test(pasaporte);
  };

  const validarCorreo = (correo) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(correo);
  };

  const obtenerErroresComprobante = () => {
    if (requiereComprobante) return {};

    const errores = {};
    const nombre = (datosComprobante.nombre || "").trim();
    const identificacion = (datosComprobante.identificacion || "").trim();
    const correo = (datosComprobante.correo || "").trim();
    const tipoIdentificacion = datosComprobante.tipo_identificacion;

    if (!nombre) {
      errores.nombre = "El nombre es obligatorio";
    }

    if (!identificacion) {
      errores.identificacion = "La identificación es obligatoria";
    } else if (tipoIdentificacion === "cedula" && !validarCedulaEcuatoriana(identificacion)) {
      errores.identificacion = "Cédula inválida";
    } else if (tipoIdentificacion === "ruc" && !validarRuc(identificacion)) {
      errores.identificacion = "RUC inválido";
    } else if (tipoIdentificacion === "pasaporte" && !validarPasaporte(identificacion)) {
      errores.identificacion = "Pasaporte inválido (6-20 caracteres alfanuméricos)";
    }

    if (!correo) {
      errores.correo = "El correo es obligatorio";
    } else if (!validarCorreo(correo)) {
      errores.correo = "Correo electrónico inválido";
    }

    return errores;
  };

  const erroresComprobante = obtenerErroresComprobante();

  useEffect(() => {
    if (open) {
      setCobroInfo(null);
      setError(null);
      setProcesandoPago(false);
      setFacturas([]);
      setNuevaFactura({ numero: "", valor: "" });
      setFacturasExpanded(false);
      setComprobanteExpanded(false);
      setTicketPerdido(false);
      setCategoriaSeleccionada(null);
      setMontoPersonalizado('');
      setCedulaPlan("");
      setInfoPlan(null);
      setBuscandoPlan(false);
      setPlanExpanded(false);
      setHorarioMisa(null);
      setTipoVehiculoActual(null);
      setCambiandoTipoVehiculo(false);
      setRecalcTick(0);
    }
  }, [open, vehicle]);

  useEffect(() => {
    const verificarHorarioMisa = async () => {
      if (!open) return;
      try {
        // Verificar según la hora de ENTRADA del vehículo, no la hora de pago.
        // Ejemplo: entra a las 7:40 (dentro de ventana 7:20-8:15) → aplica tarifa misa
        //          entra a las 8:20 (fuera de ventana) → tarifa normal
        const fechaEntrada = vehicle?.fecha_entrada;
        const params = fechaEntrada
          ? `?fecha=${encodeURIComponent(fechaEntrada)}`
          : '';
        const res = await apiClient.get(`/api/horarios-misa/vigente${params}`);
        if (res?.data?.success && res.data.en_horario_misa) {
          setHorarioMisa(res.data.horario);
        } else {
          setHorarioMisa(null);
        }
      } catch {
        setHorarioMisa(null);
      }
    };
    verificarHorarioMisa();
  }, [open, vehicle?.fecha_entrada]);

  useEffect(() => {
    const cargarConfiguracion = async () => {
      if (!open) return;

      try {
        const res = await apiClient.get("api/configuracion/cobros/parametros");
        if (res?.data?.success) {
          const minutosGratis =
            Number(res?.data?.data?.free_initial_time?.valor) || 0;
          setConfig({
            tiempoGratisHabilitado:
              res?.data?.data?.is_free_time_enabled?.valor === "1",
            precioPorHora: Number(res?.data?.data?.price_per_hour?.valor) || 1,
            typePriceCalc:
              res?.data?.data?.type_price_calc?.valor === "fixed"
                ? "fixed"
                : "hourly",
            fixedPrice: Number(res?.data?.data?.fixed_price?.valor) || 0,
            lostTicketValue: Number(res?.data?.data?.lost_ticket_value?.valor) || 0,
            invoiceDiscountHours:
              Number(res?.data?.data?.invoice_discount_hours?.valor) || 1,
            minutosGratis: minutosGratis,
            minutosSalida:
              Number(res?.data?.data?.exit_grace_minutes?.valor) || 15,
            totalEspacios:
              Number(res?.data?.data?.total_parking_space?.valor) || 0,
            espacioParqueoUsado:
              Number(res?.data?.data?.parking_space_used?.valor) || 0,
          });
        }
      } catch (error) {
        console.error("Error al cargar configuración:", error);
        setError("Error al cargar la configuración de cobros");
      }
    };

    cargarConfiguracion();
  }, [open]);

  useEffect(() => {
    const cargarCategorias = async () => {
      if (!open) return;
      try {
        const res = await apiClient.get("/api/configuracion/categorias-tiempo-gratis");
        if (res?.data?.success) setCategorias(res.data.data);
      } catch (e) {
        console.error("Error cargando categorías de tiempo gratis:", e);
      }
    };
    cargarCategorias();
  }, [open]);

  useEffect(() => {
    const cargarCategoriasVehiculo = async () => {
      if (!open) return;
      try {
        const res = await apiClient.get("/api/configuracion/grupos-tarifa-vehiculo/categorias");
        if (res?.data?.success) setCategoriasVehiculo(res.data.data);
      } catch (e) {
        console.error("Error cargando categorías de vehículo:", e);
      }
    };
    cargarCategoriasVehiculo();
  }, [open]);

  const minutosGratisEfectivos = categoriaSeleccionada
    ? categoriaSeleccionada.minutos_gratis
    : config.minutosGratis;

  // Horas cobradas localmente cuando hay categoría seleccionada (evita re-llamar al servidor)
  const horasCobradasEfectivas = (() => {
    if (!cobroInfo) return 0;
    // Los minutos gratis de la categoría aplican porque el cajero la eligió
    // a propósito; antes dependían del interruptor global de tiempo gratis,
    // que es otra cosa (la cortesía automática de todos los vehículos).
    if (categoriaSeleccionada) {
      const minCobrados = Math.max(0, cobroInfo.tiempo_total_minutos - (categoriaSeleccionada.minutos_gratis || 0));
      return Math.ceil(minCobrados / 60);
    }
    return cobroInfo.horas_cobradas;
  })();

  const esGratisLocal = cobroInfo
    ? (categoriaSeleccionada
        ? cobroInfo.tiempo_total_minutos <= (categoriaSeleccionada.minutos_gratis || 0)
        : cobroInfo.es_gratis)
    : false;

  useEffect(() => {
    const calcularCobro = async () => {
      if (!open || !vehicle?.codigo_qr) return;
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          precio_por_hora: config.precioPorHora,
          type_price_calc: config.typePriceCalc,
          fixed_price: config.fixedPrice,
          minutos_gratis: config.minutosGratis,
          minutos_gratis_habilitados: config.tiempoGratisHabilitado ? "1" : "0",
        });
        const res = await apiClient.get(
          `api/vehiculos/calcular-cobro/${vehicle.codigo_qr}?${params}`,
        );
        if (res?.data?.success) {
          const data = res.data?.data;
          if (
            data.tiempo_total_minutos < config.minutosGratis &&
            config.tiempoGratisHabilitado
          ) {
            data.es_gratis = true;
          }
          setCobroInfo(data);
          if (data?.vehiculo?.tipo) setTipoVehiculoActual(data.vehiculo.tipo);
        } else {
          setError("No se pudo calcular el cobro");
        }
      } catch (error) {
        console.error("Error al calcular cobro:", error);
        setError(error.response?.data?.message || "Error al calcular el cobro");
      } finally {
        setLoading(false);
      }
    };

    if (config.precioPorHora && vehicle) {
      calcularCobro();
    }
  }, [
    open,
    vehicle,
    config.precioPorHora,
    config.typePriceCalc,
    config.fixedPrice,
    config.minutosGratis,
    config.tiempoGratisHabilitado,
    recalcTick,
  ]);

  const handleCambiarTipoVehiculo = async (nuevoSlug) => {
    if (!vehicle?.codigo_qr || nuevoSlug === tipoVehiculoActual) return;
    setCambiandoTipoVehiculo(true);
    setError(null);
    try {
      await apiClient.put(`api/vehiculos/${vehicle.codigo_qr}/tipo-vehiculo`, {
        tipo_vehiculo: nuevoSlug,
      });
      setTipoVehiculoActual(nuevoSlug);
      setRecalcTick((t) => t + 1);
      enqueueSnackbar("Tipo de vehículo actualizado, tarifa recalculada", {
        variant: "success",
        autoHideDuration: 3000,
        anchorOrigin: { vertical: "bottom", horizontal: "left" },
      });
    } catch (error) {
      console.error("Error actualizando tipo de vehículo:", error);
      setError(error.response?.data?.error || "Error al actualizar el tipo de vehículo");
    } finally {
      setCambiandoTipoVehiculo(false);
    }
  };

  const handleClose = () => {
    if (!procesandoPago) {
      onClose();
    }
  };

  const handleAgregarFactura = () => {
    if (
      !nuevaFactura.numero ||
      !nuevaFactura.valor ||
      nuevaFactura.valor <= 0
    ) {
      setError(
        "Por favor ingrese todos los campos de la factura con valores válidos",
      );
      return;
    }

    const numeroFactura = nuevaFactura.numero.trim();
    const facturaExistente = facturas.find((f) => f.numero === numeroFactura);
    if (facturaExistente) {
      setError(
        `El número de factura "${numeroFactura}" ya ha sido agregado`,
      );
      return;
    }

    setFacturas([
      ...facturas,
      {
        numero: numeroFactura,
        valor: parseFloat(nuevaFactura.valor),
      },
    ]);
    setNuevaFactura({ numero: "", valor: "" });
    setError(null);
  };

  const handleEliminarFactura = (numero) => {
    setFacturas(facturas.filter((f) => f.numero !== numero));
  };

  const calcularTotalFacturas = () => {
    return facturas.reduce((sum, f) => sum + f.valor, 0);
  };

  const calcularHorasDescontadas = () => {
    return facturas.length * Number(config.invoiceDiscountHours || 0);
  };

  // Si el vehículo cae en un grupo de tarifa por tipo de vehículo, el backend
  // (calcular-cobro) ya resuelve y devuelve la tarifa efectiva en cobroInfo —
  // esos valores tienen prioridad sobre la tarifa global fetcheada en config.
  // La categoría de cobro que elige el cajero manda sobre la tarifa resuelta
  // por el backend (grupo por tipo de vehículo) y sobre la global.
  const tipoCategoria = categoriaSeleccionada?.tipo_calculo || null;
  const esPersonalizada = tipoCategoria === "personalizada";
  const typePriceCalcEfectivo =
    tipoCategoria === "fixed" ? "fixed"
    : tipoCategoria === "hourly" ? "hourly"
    : (cobroInfo?.type_price_calc ?? config.typePriceCalc);
  const fixedPriceEfectivo =
    tipoCategoria === "fixed"
      ? Number(categoriaSeleccionada?.precio_fijo || 0)
      : (cobroInfo?.fixed_price ?? config.fixedPrice);

  const calcularHorasEfectivas = () => {
    if (!cobroInfo) return 0;
    if (typePriceCalcEfectivo === "fixed") {
      return horasCobradasEfectivas || 0;
    }
    const horasDescontadas = calcularHorasDescontadas();
    return Math.max(0, horasCobradasEfectivas - horasDescontadas);
  };

  // Prioridad: override manual del cajero (categoriaSeleccionada) > tarifa
  // resuelta por el backend (grupo de tipo de vehículo, si aplica) > tarifa global
  const precioPorHoraEfectivo =
    categoriaSeleccionada?.precio_por_hora ?? cobroInfo?.precio_por_hora ?? config.precioPorHora;

  const calcularTotalConDescuento = () => {
    if (!cobroInfo) return 0;
    if (esPersonalizada) return Number(montoPersonalizado || 0);
    if (typePriceCalcEfectivo === "fixed") {
      return cobroInfo.es_gratis ? 0 : Number(fixedPriceEfectivo || 0);
    }
    const horasEfectivas = calcularHorasEfectivas();
    return horasEfectivas * precioPorHoraEfectivo;
  };

  const calcularCargoTicketPerdido = () => {
    return ticketPerdido ? Number(config.lostTicketValue || 0) : 0;
  };

  const calcularTotalFinal = () => {
    const totalBase = calcularTotalConDescuento();
    const cargoTicketPerdido = calcularCargoTicketPerdido();
    return Number((totalBase + cargoTicketPerdido).toFixed(2));
  };

  const esGratisConDescuento = () => {
    if (!cobroInfo) return false;
    // Si está cubierto por plan o tarjeta prepago seleccionada, es "gratis"
    if (infoPlan?.cubierto_ahora) return true;
    if (usarTarjetaPrepago && tarjetaInfo?.activo && (tarjetaInfo.horas_totales - tarjetaInfo.horas_usadas) > 0) return true;
    if (calcularCargoTicketPerdido() > 0) {
      return false;
    }
    if (esPersonalizada) {
      return Number(montoPersonalizado || 0) === 0;
    }
    if (typePriceCalcEfectivo === "fixed") {
      return esGratisLocal;
    }
    return esGratisLocal || calcularHorasEfectivas() === 0;
  };

  // Calcula la tarifa de misa según tiempo de permanencia.
  // Regla:
  //   Hasta horas_limite h          → tarifa_unica  (ej. $1.00)
  //   Cada hora o fracción adicional → + tarifa_excedido por fracción (ej. $1.50 c/u)
  //
  // Ej. entra 7:40, paga 9:51 → 131 min. Límite = 120 min.
  //     Extra = 11 min → ceil(11/60) = 1 fracción → $1.00 + 1×$1.50 = $2.50 ✓
  //
  // El límite es exactamente horas_limite horas; NO se añaden minutos de gracia.
  const calcularTarifaMisa = () => {
    if (!horarioMisa) return 0;
    // Franja sin cobro: manda sobre cualquier tarifa
    if (horarioMisa.gratis) return 0;
    const tarifaBase     = Number(horarioMisa.tarifa_unica || 0);
    const horasLimite    = Number(horarioMisa.horas_limite || 2);
    const tarifaFraccion = Number(horarioMisa.tarifa_excedido || 0);
    const limiteMinutos  = horasLimite * 60;
    const tiempoTotal    = cobroInfo?.tiempo_total_minutos ?? 0;
    if (tiempoTotal <= limiteMinutos) return tarifaBase;
    const minutosExtra    = tiempoTotal - limiteMinutos;
    const fraccionesExtra = Math.ceil(minutosExtra / 60);
    return round2(tarifaBase + fraccionesExtra * tarifaFraccion);
  };

  const round2 = (n) => Math.round(n * 100) / 100;

  const totalMostrado = () => {
    if (infoPlan?.cubierto_ahora) return calcularCargoTicketPerdido();
    // La franja sin cobro manda sobre todo, incluso sobre una categoría
    // elegida a mano: si el horario es libre, no se cobra.
    if (horarioMisa?.gratis) return calcularCargoTicketPerdido();
    // Una categoría elegida por el cajero es una decisión explícita y gana
    // sobre la tarifa por horario. Sin esto, cambiar de categoría no movía el
    // total mientras hubiera una franja horaria vigente.
    if (horarioMisa && !categoriaSeleccionada) {
      return round2(calcularTarifaMisa() + calcularCargoTicketPerdido());
    }
    return calcularTotalFinal();
  };

  const buscarPlan = async () => {
    const placa = placaPlan.trim().toUpperCase();
    const cedula = cedulaPlan.trim();
    if (!placa && !cedula) return;
    setBuscandoPlan(true);
    setInfoPlan(null);
    try {
      // Prioridad: si hay placa, verificar por placa; si no, por cédula
      const url = placa
        ? `/api/planes/suscripciones/verificar-placa/${placa}`
        : `/api/planes/suscripciones/verificar/${cedula}`;
      const res = await apiClient.get(url);
      if (res.data?.success) {
        const planData = res.data.data;
        setInfoPlan(planData);
        setTarjetaInfo(null);
        setUsarTarjetaPrepago(false);
        // Si el plan es de tipo tarjeta prepago, cargar saldo
        if (planData?.plan?.tipo === 'tarjeta' && planData?.suscripcion?.id) {
          try {
            const tRes = await apiClient.get(`/api/planes/suscripciones/${planData.suscripcion.id}/tarjeta`);
            if (tRes.data?.success && tRes.data?.data) {
              setTarjetaInfo(tRes.data.data);
            }
          } catch {}
        }
      }
    } catch {
      setInfoPlan({ tiene_plan: false, cubierto_ahora: false, mensaje: "Error al consultar planes" });
    } finally {
      setBuscandoPlan(false);
    }
  };

  // Si el plan cubre, el total es $0 (ya pagó su mensualidad/diario)
  const totalConPlan = infoPlan?.cubierto_ahora ? 0 : null;

const imprimirComprobante = (cobroId = null, valorReal = null) => {
  const ahora = new Date();
  const fecha = ahora.toLocaleDateString("es-ES");
  const hora = ahora.toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });

  const fechaEntrada = new Date(vehicle?.fecha_entrada);
  const fechaEntradaFormato = fechaEntrada.toLocaleDateString("es-ES");
  const horaEntrada = fechaEntrada.toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit"
  });

  const fechaSalida = new Date();
  const fechaSalidaFormato = fechaSalida.toLocaleDateString("es-ES");
  const horaSalida = fechaSalida.toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit"
  });

  const minutosGraciaConfig = config.minutosSalida || 15;
  const horaMaximaSalida = new Date(ahora.getTime() + minutosGraciaConfig * 60000);
  const horaMaximaSalidaFormato = horaMaximaSalida.toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit"
  });

  const clienteNombre = requiereComprobante
    ? "CONSUMIDOR FINAL"
    : datosComprobante.nombre.toUpperCase();

  const clienteId = requiereComprobante
    ? "9999999999"
    : datosComprobante.identificacion;

  const placa =
    vehicle?.numero_placa ||
    vehicle?.numero_placa_auxiliar ||
    "SIN PLACA";
  const secuencial = cobroId ? String(cobroId).padStart(10, '0') : '----------';
  const html = `
<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>Recibo Parqueo</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
html,body{width:58mm;margin:0;padding:0}
body{font-family:Arial,Helvetica,sans-serif;font-size:10px;line-height:1.2;padding:3px;color:#000;background:#fff}
.container{width:100%;margin:0 auto}
.header{text-align:center;border-bottom:2px solid #000;padding-bottom:3px;margin-bottom:4px}
.logo{font-size:20px;font-weight:900;letter-spacing:2px}
.company{font-size:11px;font-weight:bold}
.title{font-size:9px;font-weight:bold;margin-top:1px}
.vehicle{border:2px solid #000;text-align:center;padding:5px 3px;margin:0 auto 4px;border-radius:2px}
.vehicle-label{font-size:8px;font-weight:bold;margin-bottom:1px}
.plate{font-size:26px;font-weight:900;font-family:"Courier New",monospace;letter-spacing:1px}
.grace-period{background:#f5f5f5;border:2px solid #000;padding:4px 3px;margin:0 auto 4px;text-align:center}
.grace-label{font-size:8px;font-weight:bold;margin-bottom:2px}
.grace-time{font-size:15px;font-weight:900;letter-spacing:1px}
.grace-subtext{font-size:7px;margin-top:2px;font-weight:bold}
.section{margin-bottom:4px;padding-bottom:3px;border-bottom:1px dashed #000}
.section-title{font-weight:900;font-size:10px;border-bottom:1px solid #000;margin-bottom:3px;padding-bottom:1px;text-align:center}
.row{display:flex;justify-content:space-between;margin-bottom:2px;font-size:10px}
.label{font-weight:bold}
.value{text-align:right}
.secuencial{text-align:center;font-size:11px;font-weight:900;font-family:"Courier New",monospace;border-bottom:1px dashed #000;padding-bottom:3px;margin-bottom:4px;letter-spacing:1px}
.breakdown{border:1px solid #000;padding:3px;font-size:9px}
.break-row{display:flex;justify-content:space-between;margin-bottom:1px}
.total{border:2px solid #000;text-align:center;padding:8px 4px;margin:6px auto;max-width:58mm}
.total-amount{font-size:32px;font-weight:900;letter-spacing:1px}
.total-label{font-size:9px;font-weight:bold}
.client{border:1px solid #000;padding:3px;margin-bottom:4px;font-size:9px}
.client-title{font-size:9px;font-weight:900;margin-bottom:2px;border-bottom:1px solid #000;text-align:center;padding-bottom:1px}
.footer{text-align:center;border-top:2px solid #000;padding-top:3px;font-size:8px}
.disclaimer{font-size:6px;margin:2px 0;font-weight:normal}
@media print{@page{size:58mm auto;margin:0}}
</style>
</head>
<body>
<div class="container">
<div class="header">
  <div class="logo">PARKEY</div>
  <div class="company">${process.env.REACT_APP_NOMBRE_CONJUNTO || ''} - ${process.env.REACT_APP_NOMBRE_CONJUNTO2 || ''}</div>
  <div class="title">RECIBO DE PARQUEO</div>
</div>
<div class="secuencial">N° ${secuencial}</div>
<div class="vehicle">
  <div class="vehicle-label">VEHÍCULO</div>
  <div class="plate">${placa}</div>
</div>
<div class="grace-period">
  <div class="grace-label">SALIDA MÁXIMA A:</div>
  <div class="grace-time">${horaMaximaSalidaFormato}</div>
  <div class="grace-subtext">Tiempo: ${minutosGraciaConfig} min</div>
</div>
<div class="section">
  <div class="section-title">TIEMPO DE PERMANENCIA</div>
  <div class="row"><span class="label">Entrada:</span><span class="value">${fechaEntradaFormato} ${horaEntrada}</span></div>
  <div class="row"><span class="label">Salida:</span><span class="value">${fechaSalidaFormato} ${horaSalida}</span></div>
  <div class="row" style="border-top:1px dashed #000;padding-top:2px;font-weight:900;text-align:center"><span style="width:100%">Total: ${cobroInfo?.detalle_tiempo}</span></div>
</div>
<div class="section">
  <div class="section-title">DESGLOSE DE TARIFA</div>
  <div class="breakdown">
    ${horarioMisa ? (() => {
      const tarifaBase     = Number(horarioMisa.tarifa_unica || 0);
      const horasLimite    = Number(horarioMisa.horas_limite || 2);
      const tarifaFraccion = Number(horarioMisa.tarifa_excedido || 0);
      const limiteMin      = horasLimite * 60;
      const tiempoTotal    = cobroInfo?.tiempo_total_minutos ?? 0;
      const excede         = tiempoTotal > limiteMin;
      const fraccionesExtra = excede ? Math.ceil((tiempoTotal - limiteMin) / 60) : 0;
      return `
        <div class="break-row" style="font-weight:900"><span>★ TARIFA MISA</span><span>${horarioMisa.nombre}</span></div>
        <div class="break-row"><span>Primeras ${horasLimite}h:</span><span>$${tarifaBase.toFixed(2)}</span></div>
        ${excede ? `
        <div class="break-row"><span>Fracciones extra (${fraccionesExtra}×$${tarifaFraccion.toFixed(2)}):</span><span>$${(fraccionesExtra * tarifaFraccion).toFixed(2)}</span></div>
        ` : ''}
      `;
    })() : `
    <div class="break-row"><span>${typePriceCalcEfectivo === "fixed" ? "Tarifa fija:" : "Tarifa/hora:"}</span><span>$${typePriceCalcEfectivo === "fixed" ? Number(fixedPriceEfectivo || 0).toFixed(2) : precioPorHoraEfectivo.toFixed(2)}${categoriaSeleccionada?.precio_por_hora != null && typePriceCalcEfectivo !== "fixed" ? ` (${categoriaSeleccionada.nombre})` : (cobroInfo?.tarifa_aplicada?.origen === "grupo" ? ` (${cobroInfo.tarifa_aplicada.grupo_nombre})` : '')}</span></div>
    <div class="break-row"><span>Horas:</span><span>${calcularHorasEfectivas()} hrs</span></div>
    ${config.tiempoGratisHabilitado && minutosGratisEfectivos > 0 ? `<div class="break-row"><span>Gratis (${categoriaSeleccionada ? categoriaSeleccionada.nombre : 'defecto'}):</span><span>-${minutosGratisEfectivos}m</span></div>` : ``}
    ${facturas.length > 0 && typePriceCalcEfectivo !== "fixed" ? `
    <div class="break-row" style="border-top:1px dashed #000;padding-top:1px"><span>Facturas:</span><span>${facturas.length}</span></div>
    <div class="break-row"><span>Dscto:</span><span>-${calcularHorasDescontadas()}h</span></div>` : ``}
    ${ticketPerdido ? `<div class="break-row" style="border-top:1px dashed #000;padding-top:1px"><span>Ticket perdido:</span><span>$${calcularCargoTicketPerdido().toFixed(2)}</span></div>` : ``}
    `}
  </div>
</div>
<div class="total">
  <div class="total-amount">$${(valorReal ?? totalMostrado()).toFixed(2)}</div>
  <div class="total-label">TOTAL A PAGAR</div>
</div>
<div class="client">
  <div class="client-title">CLIENTE</div>
  <div class="row"><span class="label">Nombre:</span><span class="value">${clienteNombre}</span></div>
  <div class="row"><span class="label">C.I.:</span><span class="value">${clienteId}</span></div>
  <div class="row"><span class="label">Método:</span><span class="value">${datosComprobante.metodo.toUpperCase()}</span></div>
</div>
<div class="footer">
  <div>${fecha} | ${hora}</div>
  <div class="disclaimer">El establecimiento no se responsabiliza por robo o pérdida de vehículos</div>
  <div>www.tglabsecuador.com</div>
</div>
</div>
<script>window.onload=()=>{setTimeout(()=>window.print(),300)}</script>
</body>
</html>
`;

  const ventana = window.open("", "_blank");
  ventana.document.write(html);
  ventana.document.close();
};

const handleConfirmarPago = async () => {
  if (!requiereComprobante && Object.keys(erroresComprobante).length > 0) {
    const primerError = Object.values(erroresComprobante)[0];
    setError(primerError || "Verifique los datos del comprobante");
    return;
  }

  setProcesandoPago(true);
  try {
    let tipo = "normal";
    if (infoPlan?.cubierto_ahora || (usarTarjetaPrepago && tarjetaInfo?.activo)) {
      tipo = "plan";
    } else if (ticketPerdido) {
      tipo = "lost_ticket";
    } else if (calcularTotalFinal() === 0) {
      tipo = "gratis";
    }
    const horasDescontadas = calcularHorasDescontadas();
    const horasCobradas = calcularHorasEfectivas();
    const totalFinal = totalMostrado();
    const cargoTicketPerdido = calcularCargoTicketPerdido();
    let datosComprobanteEnviar;
    if (!requiereComprobante) {
      datosComprobanteEnviar = {
        ...datosComprobante,
        descripcion:
          (datosComprobante?.descripcion || "Pago Parqueadero").trim() ||
          "Pago Parqueadero",
        items: [{
          cantidad: 1,
          valor_unitario: totalFinal,
          valor_total: totalFinal,
        }]
      };
    } else {
      datosComprobanteEnviar = {
        nombre: "Consumidor Final",
        tipo_identificacion: "consumidor_final",
        identificacion: "9999999999",
        correo: "",
        metodo: datosComprobante.metodo,
        descripcion:
          (datosComprobante?.descripcion || "Pago Parqueadero").trim() ||
          "Pago Parqueadero",
        items: [{
          cantidad: 1,
          valor_unitario: totalFinal,
          valor_total: totalFinal,
        }]
      };
    }

    if (ticketPerdido) {
      datosComprobanteEnviar.descripcion = `${datosComprobanteEnviar.descripcion} | Ticket perdido: $${cargoTicketPerdido.toFixed(2)}`;
    }

    const payload = {
      codigo_qr: vehicle?.codigo_qr,
      facturas: facturas,
      tipo: tipo,
      ticket_perdido: ticketPerdido,
      valor_ticket_perdido: cargoTicketPerdido,
      horas_permanencia: cobroInfo?.horas_cobradas,
      horas_descontadas: horasDescontadas,
      horas_cobradas: horasCobradas,
      total_pagar: totalFinal,
      type_price_calc: config.typePriceCalc,
      fixed_price: config.fixedPrice,
      datos_comprobante: datosComprobanteEnviar,
    };

    const response = await apiClient.post(`/api/vehiculos/cobrar`, payload);
    const cobroId = response?.data?.data?.cobro?.id || null;
    const cobroValor = response?.data?.data?.cobro?.valor ?? null;

    enqueueSnackbar("Pago registrado exitosamente", {
      variant: "success",
      autoHideDuration: 4000,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left",
      },
    });

    if (typeof onPaymentSuccess === "function") {
      onPaymentSuccess(totalFinal);
    }
    
    // Imprimir comprobante inmediatamente
    imprimirComprobante(cobroId, cobroValor);
    
    // Actualizar datos después
    setTimeout(() => {
      loadVehicles(1, false);
      onClose();
    }, 2000);

  } catch (error) {
    console.error("Error al procesar el pago:", error);
    setError(error.response?.data?.message || "Error al procesar el pago");
  } finally {
    setProcesandoPago(false);
  }
};

  if (!vehicle) return null;

  return (

<Dialog
  open={open}
  onClose={handleClose}
  maxWidth="md"
  fullWidth
  scroll="paper"
  sx={{
    "& .MuiDialog-container": {
      alignItems: "center",
    },
  }}
  PaperProps={{
    sx: {
      maxHeight: "85vh",
      m: 2,
    },
  }}
>
  <DialogTitle
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      background: "linear-gradient(135deg, #393939 0%, #4a4a4a 100%)",
      color: "white",
      pb: 0,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <AttachMoney />
      <Box>
        <Typography variant="h6" fontWeight="bold">
          Cobro de Parqueadero
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.9 }}>
          Procese el pago del vehículo
        </Typography>
      </Box>
    </Box>
    <IconButton onClick={handleClose} size="small" sx={{ color: "white" }}>
      <Close />
    </IconButton>
  </DialogTitle>
  <Box
    sx={{
      background: "linear-gradient(135deg, #f3a92a 0%, #e69500 100%)",
      color: "white",
      px: 3,
      py: 2,
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      gap: 2,
    }}
  >
    <Box>
      <Typography variant="caption" sx={{ opacity: 0.9 }}>
        Placa del Vehículo
      </Typography>
      <Typography variant="h6" fontWeight="bold">
        {vehicle?.numero_placa || vehicle?.numero_placa_auxiliar || "Sin placa"}
      </Typography>
    </Box>
    <Box>
      <Typography variant="caption" sx={{ opacity: 0.9 }}>
        Código QR
      </Typography>
      <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: "monospace" }}>
        {vehicle?.codigo_qr}
      </Typography>
    </Box>
    <Box>
      <Typography variant="caption" sx={{ opacity: 0.9 }}>
        Color del Vehículo
      </Typography>
      <Typography variant="h6" fontWeight="bold">
        {vehicle?.color_vehiculo || "No especificado"}
      </Typography>
    </Box>
    <Box>
      <Typography variant="caption" sx={{ opacity: 0.9 }}>
        Tipo de Vehículo
      </Typography>
      <Typography variant="h6" fontWeight="bold">
        {cobroInfo?.vehiculo?.tipo_label || vehicle?.tipo_vehiculo_label || vehicle?.tipo_vehiculo || "No especificado"}
      </Typography>
    </Box>
  </Box>

  <DialogContent
    sx={{
      py: 3,
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
    }}
  >
    {error && (
      <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
        {error}
      </Alert>
    )}

    {horarioMisa && (() => {
      const tarifaBase     = Number(horarioMisa.tarifa_unica || 0);
      const horasLimite    = Number(horarioMisa.horas_limite || 2);
      const tarifaFraccion = Number(horarioMisa.tarifa_excedido || 0);
      const limiteMinutos  = horasLimite * 60;   // sin gracia de salida
      const tiempoTotal    = cobroInfo?.tiempo_total_minutos ?? 0;
      const excede         = tiempoTotal > limiteMinutos;
      const fraccionesExtra = excede ? Math.ceil((tiempoTotal - limiteMinutos) / 60) : 0;
      const horaIngreso    = vehicle?.fecha_entrada
        ? new Date(vehicle.fecha_entrada).toLocaleTimeString("es-EC", {
            hour: "2-digit", minute: "2-digit", hour12: false,
            timeZone: "America/Guayaquil",
          })
        : "—";
      return (
        <Alert
          severity={excede ? "error" : "warning"}
          sx={{ mb: 2, width: "100%" }}
          icon={false}
        >
          <strong>Horario de Misa: {horarioMisa.nombre}</strong>
          {" "}· Ingreso: <strong>{horaIngreso}</strong>
          <br />
          Primeras {horasLimite}h: <strong>${tarifaBase.toFixed(2)}</strong>
          {" · "}
          Hora/fracción adicional: <strong>+${tarifaFraccion.toFixed(2)}</strong>
          <br />
          {excede ? (
            <>
              <strong style={{ color: "#c62828" }}>⚠ Tiempo excedido</strong>
              {" — "}
              {Math.floor(tiempoTotal / 60)}h {tiempoTotal % 60}min total
              {" · "}
              {fraccionesExtra} fracción(es) extra × ${tarifaFraccion.toFixed(2)}
              {" → "}
              <strong>Total: ${calcularTarifaMisa().toFixed(2)}</strong>
            </>
          ) : (
            <>
              Dentro de las {horasLimite}h — total:{" "}
              <strong>${tarifaBase.toFixed(2)}</strong>
            </>
          )}
        </Alert>
      );
    })()}

    {loading ? (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Calculando cobro...
        </Typography>
      </Box>
    ) : (
      <Grid container spacing={3} >
        <Grid item xs={12} md={7}>
          {cobroInfo ? (
            <>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Schedule color="primary" />
                    <Typography variant="h6" color="primary">
                      Información de Tiempo
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                        <Typography variant="caption" color="textSecondary">
                          Fecha y Hora de Entrada
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {vehicle?.fecha_entrada
                            ? new Date(vehicle.fecha_entrada).toLocaleString("es-ES")
                            : "N/A"}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                        <Typography variant="caption" color="textSecondary">
                          Tiempo Total
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                          {cobroInfo.detalle_tiempo}
                        </Typography>
                      </Paper>
                    </Grid>

                    <Grid item xs={6}>
                      <Paper sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                        <Typography variant="caption" color="textSecondary">
                          Tiempo Cobrado
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                          {Math.floor(cobroInfo.minutos_cobrados / 60)}h{" "}
                          {cobroInfo.minutos_cobrados % 60}m
                        </Typography>
                      </Paper>
                    </Grid>

                    {config.tiempoGratisHabilitado && (
                      <Grid item xs={12}>
                        <Alert
                          severity="info"
                          icon={<Info />}
                          sx={{ py: 0.5 }}
                        >
                          <Typography variant="body2">
                            Minutos gratis aplicados:{" "}
                            <strong>
                              {cobroInfo.minutos_gratis_aplicados} min
                            </strong>
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent
                  onClick={() =>
                    setComprobanteExpanded(!comprobanteExpanded)
                  }
                  sx={{
                    cursor: "pointer",
                    bgcolor: "#e3f2fd",
                    "&:hover": { bgcolor: "#bbdefb" },
                    transition: "background-color 0.2s",
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Receipt color="primary" />
                      <Typography variant="h6" color="primary">
                        Datos del Comprobante
                      </Typography>
                      {!requiereComprobante && (
                        <Typography
                          variant="caption"
                          sx={{
                            bgcolor: "primary.main",
                            color: "white",
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontWeight: "bold",
                          }}
                        >
                          Requerido
                        </Typography>
                      )}
                    </Box>
                    <ExpandMore
                      sx={{
                        transform: comprobanteExpanded
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.3s",
                      }}
                    />
                  </Box>
                </CardContent>
                <Collapse in={comprobanteExpanded}>
                  <CardContent sx={{ bgcolor: "#f5f5f5", pt: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={requiereComprobante}
                          onChange={(e) =>
                            setRequiereComprobante(e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="Consumidor Final (desmarcar para comprobante con datos)"
                      sx={{ mb: 2 }}
                    />
                    {!requiereComprobante && (
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Nombre Completo"
                            value={datosComprobante.nombre}
                            error={Boolean(erroresComprobante.nombre)}
                            helperText={erroresComprobante.nombre || " "}
                            onChange={(e) =>
                              setDatosComprobante({
                                ...datosComprobante,
                                nombre: e.target.value,
                              })
                            }
                            required
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Person fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Tipo de Identificación</InputLabel>
                            <Select
                              value={datosComprobante.tipo_identificacion}
                              label="Tipo de Identificación"
                              onChange={(e) =>
                                setDatosComprobante({
                                  ...datosComprobante,
                                  tipo_identificacion: e.target.value,
                                })
                              }
                            >
                              <MenuItem value="cedula">Cédula</MenuItem>
                              <MenuItem value="ruc">RUC</MenuItem>
                              <MenuItem value="pasaporte">
                                Pasaporte
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Número de Identificación"
                            value={datosComprobante.identificacion}
                            error={Boolean(erroresComprobante.identificacion)}
                            helperText={erroresComprobante.identificacion || " "}
                            onChange={(e) =>
                              {
                                const valor = e.target.value || "";
                                const tipo = datosComprobante.tipo_identificacion;

                                let identificacion = valor;
                                if (tipo === "cedula") {
                                  identificacion = valor.replace(/\D/g, "").slice(0, 10);
                                } else if (tipo === "ruc") {
                                  identificacion = valor.replace(/\D/g, "").slice(0, 13);
                                }

                                setDatosComprobante({
                                  ...datosComprobante,
                                  identificacion,
                                });
                              }
                            }
                            required
                            inputProps={{
                              inputMode:
                                datosComprobante.tipo_identificacion === "pasaporte"
                                  ? "text"
                                  : "numeric",
                              pattern:
                                datosComprobante.tipo_identificacion === "pasaporte"
                                  ? undefined
                                  : "[0-9]*",
                              maxLength:
                                datosComprobante.tipo_identificacion === "cedula"
                                  ? 10
                                  : datosComprobante.tipo_identificacion === "ruc"
                                  ? 13
                                  : 20,
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <BrandingWatermark fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Correo Electrónico"
                            type="email"
                            value={datosComprobante.correo}
                            error={Boolean(erroresComprobante.correo)}
                            helperText={erroresComprobante.correo || " "}
                            onChange={(e) =>
                              setDatosComprobante({
                                ...datosComprobante,
                                correo: e.target.value,
                              })
                            }
                            required
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Email fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                    )}
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Método de Pago</InputLabel>
                          <Select
                            value={datosComprobante.metodo}
                            label="Método de Pago"
                            onChange={(e) =>
                              setDatosComprobante({
                                ...datosComprobante,
                                metodo: e.target.value,
                              })
                            }
                            startAdornment={
                              <InputAdornment position="start">
                                <Payment fontSize="small" />
                              </InputAdornment>
                            }
                          >
                            <MenuItem value="efectivo">Efectivo</MenuItem>
                            <MenuItem value="tarjeta">Tarjeta</MenuItem>
                            <MenuItem value="transferencia">Transferencia</MenuItem>
                            <MenuItem value="de_una">De Una</MenuItem>
                            <MenuItem value="otro">Otro método de pago</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Descripción"
                          value={datosComprobante.descripcion}
                          onChange={(e) =>
                            setDatosComprobante({
                              ...datosComprobante,
                              descripcion: e.target.value,
                            })
                          }
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Collapse>
              </Card>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent
                  onClick={() => setFacturasExpanded(!facturasExpanded)}
                  sx={{
                    cursor: "pointer",
                    bgcolor: "#fef3c7",
                    "&:hover": { bgcolor: "#fde68a" },
                    transition: "background-color 0.2s",
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Receipt color="warning" />
                      <Typography variant="h6" color="warning.dark">
                        Validar Facturas (Opcional)
                      </Typography>
                    </Box>
                    <ExpandMore
                      sx={{
                        transform: facturasExpanded
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.3s",
                      }}
                    />
                  </Box>
                  {!facturasExpanded && facturas.length > 0 && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      {facturas.length} factura(s) - Total: $
                      {calcularTotalFacturas().toFixed(2)} -{" "}
                      {calcularHorasDescontadas()} hrs descontadas
                    </Typography>
                  )}
                </CardContent>
                <Collapse in={facturasExpanded}>
                  <CardContent sx={{ bgcolor: "#fffbeb", pt: 0 }}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ display: "block", mb: 2 }}
                    >
                      Por cada factura validada se descuenta automáticamente {config.invoiceDiscountHours} hora(s)
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Número de Factura"
                          value={nuevaFactura.numero}
                          onChange={(e) =>
                            setNuevaFactura({
                              ...nuevaFactura,
                              numero: e.target.value,
                            })
                          }
                          placeholder="Ej: FAC-001"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Receipt fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Valor de Factura"
                          type="number"
                          value={nuevaFactura.valor}
                          onChange={(e) =>
                            setNuevaFactura({
                              ...nuevaFactura,
                              valor: e.target.value,
                            })
                          }
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                          }}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                    </Grid>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Add />}
                      onClick={handleAgregarFactura}
                      disabled={
                        !nuevaFactura.numero ||
                        !nuevaFactura.valor
                      }
                      sx={{ mb: 2 }}
                    >
                      Agregar Factura
                    </Button>
                    {facturas.length > 0 && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Facturas agregadas ({facturas.length})
                        </Typography>
                        <List dense>
                          {facturas.map((factura) => (
                            <ListItem
                              key={factura.numero}
                              sx={{
                                bgcolor: "white",
                                mb: 1,
                                borderRadius: 1,
                                border: "1px solid #e0e0e0",
                              }}
                            >
                              <ListItemText
                                primary={`${factura.numero} - $${factura.valor.toFixed(2)}`}
                                secondary={`${config.invoiceDiscountHours} hora(s) descontada(s)`}
                              />
                              <ListItemSecondaryAction>
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onClick={() =>
                                    handleEliminarFactura(factura.numero)
                                  }
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            bgcolor: "white",
                            borderRadius: 1,
                          }}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography
                                variant="body2"
                                color="textSecondary"
                              >
                                Total Facturas:
                              </Typography>
                              <Typography variant="h6" fontWeight="bold">
                                ${calcularTotalFacturas().toFixed(2)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography
                                variant="body2"
                                color="textSecondary"
                              >
                                Horas Descontadas:
                              </Typography>
                              <Typography variant="h6" fontWeight="bold">
                                {calcularHorasDescontadas()} hrs
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Collapse>
              </Card>

              {/* ── Sección Plan por cédula ────────────────────────────── */}
              <Card
                variant="outlined"
                sx={{
                  mb: 2,
                  borderColor: infoPlan?.cubierto_ahora
                    ? "primary.main"
                    : infoPlan?.tiene_plan
                    ? "warning.main"
                    : "divider",
                }}
              >
                <CardContent
                  onClick={() => setPlanExpanded(!planExpanded)}
                  sx={{ cursor: "pointer", bgcolor: infoPlan?.cubierto_ahora ? "#e3f2fd" : "inherit", "&:hover": { bgcolor: infoPlan?.cubierto_ahora ? "#bbdefb" : "action.hover" } }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Receipt fontSize="small" color={infoPlan?.cubierto_ahora ? "primary" : "action"} />
                      <Typography variant="subtitle2" fontWeight="bold">
                        Verificar Plan (Cédula / RUC)
                      </Typography>
                      {infoPlan?.cubierto_ahora && (
                        <Chip label="PLAN ACTIVO" size="small" color="primary" />
                      )}
                      {infoPlan?.tiene_plan && !infoPlan?.cubierto_ahora && (
                        <Chip label="FUERA DE HORARIO" size="small" color="warning" />
                      )}
                    </Box>
                    <ExpandMore
                      sx={{ transform: planExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s" }}
                    />
                  </Box>
                  {infoPlan && !planExpanded && (
                    <Typography variant="caption" color={infoPlan.cubierto_ahora ? "primary" : "text.secondary"} sx={{ mt: 0.5, display: "block" }}>
                      {infoPlan.mensaje}
                    </Typography>
                  )}
                </CardContent>
                <Collapse in={planExpanded}>
                  <CardContent sx={{ pt: 1 }}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={12} sm={5}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Cédula / RUC / Pasaporte"
                          value={cedulaPlan}
                          onChange={(e) => { setCedulaPlan(e.target.value); setInfoPlan(null); }}
                          onKeyDown={(e) => e.key === "Enter" && buscarPlan()}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Person fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={5}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Número de placa"
                          placeholder="Ej: ABC1234"
                          value={placaPlan}
                          onChange={(e) => { setPlacaPlan(e.target.value.toUpperCase()); setInfoPlan(null); }}
                          onKeyDown={(e) => e.key === "Enter" && buscarPlan()}
                          inputProps={{ style: { textTransform: "uppercase" } }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <DirectionsCar fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                          helperText="Tiene prioridad sobre la cédula"
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Button
                          fullWidth
                          variant="outlined"
                          size="small"
                          onClick={buscarPlan}
                          disabled={buscandoPlan || (!cedulaPlan.trim() && !placaPlan.trim())}
                          startIcon={buscandoPlan ? <CircularProgress size={14} /> : <Info fontSize="small" />}
                        >
                          Verificar
                        </Button>
                      </Grid>
                    </Grid>

                    {infoPlan && (
                      <Alert
                        severity={infoPlan.cubierto_ahora ? "success" : infoPlan.tiene_plan ? "warning" : "info"}
                        sx={{ mt: 1.5 }}
                      >
                        {infoPlan.mensaje}
                        {infoPlan.cubierto_ahora && (
                          <Typography variant="body2" fontWeight="bold" mt={0.5}>
                            Total a cobrar: $0.00 (cubierto por plan)
                          </Typography>
                        )}
                      </Alert>
                    )}

                    {/* ── Tarjeta Prepago ── */}
                    {tarjetaInfo && infoPlan?.plan?.tipo === 'tarjeta' && (
                      <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fff8e7', borderRadius: 2, border: '1px solid #F59E0B' }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#282626', mb: 1 }}>
                          💳 Tarjeta Prepago — {infoPlan?.suscripcion?.nombre_titular}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                          <Box sx={{ textAlign: 'center', px: 2, py: 1, bgcolor: 'white', borderRadius: 2, border: '1px solid #E5E5E5', minWidth: 90 }}>
                            <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>Disponibles</Typography>
                            <Typography variant="h6" fontWeight={800} sx={{ color: '#22c55e' }}>
                              {(tarjetaInfo.horas_totales - tarjetaInfo.horas_usadas).toFixed(2)}h
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center', px: 2, py: 1, bgcolor: 'white', borderRadius: 2, border: '1px solid #E5E5E5', minWidth: 90 }}>
                            <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>Usadas</Typography>
                            <Typography variant="h6" fontWeight={800} sx={{ color: '#94a3b8' }}>
                              {Number(tarjetaInfo.horas_usadas).toFixed(2)}h
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center', px: 2, py: 1, bgcolor: 'white', borderRadius: 2, border: '1px solid #E5E5E5', minWidth: 90 }}>
                            <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>Total</Typography>
                            <Typography variant="h6" fontWeight={800} sx={{ color: '#282626' }}>
                              {Number(tarjetaInfo.horas_totales).toFixed(2)}h
                            </Typography>
                          </Box>
                        </Box>
                        {tarjetaInfo.activo && (tarjetaInfo.horas_totales - tarjetaInfo.horas_usadas) > 0 ? (
                          <Button
                            fullWidth variant="contained" size="small"
                            onClick={() => setUsarTarjetaPrepago(!usarTarjetaPrepago)}
                            sx={{
                              borderRadius: 2, textTransform: 'none', fontWeight: 700,
                              bgcolor: usarTarjetaPrepago ? '#16a34a' : '#F59E0B',
                              color: 'white',
                              '&:hover': { bgcolor: usarTarjetaPrepago ? '#15803d' : '#d97706' },
                            }}>
                            {usarTarjetaPrepago ? '✓ Pagar con tarjeta prepago (seleccionado)' : 'Pagar con tarjeta prepago'}
                          </Button>
                        ) : (
                          <Alert severity="warning" sx={{ borderRadius: 2 }}>
                            {!tarjetaInfo.activo ? 'Tarjeta inactiva' : 'Saldo insuficiente en tarjeta prepago'}
                          </Alert>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Collapse>
              </Card>

              {categorias.length > 0 && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent
                    onClick={() => setCategoriaExpanded(!categoriaExpanded)}
                    sx={{
                      cursor: "pointer",
                      bgcolor: "#e8f5e9",
                      "&:hover": { bgcolor: "#c8e6c9" },
                      transition: "background-color 0.2s",
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center" gap={1}>
                        <AttachMoney color="success" />
                        <Typography variant="h6" color="success.dark">
                          Categoría de cobro
                        </Typography>
                      </Box>
                      <ExpandMore
                        sx={{
                          transform: categoriaExpanded ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.3s",
                          color: "success.dark",
                        }}
                      />
                    </Box>
                    {!categoriaExpanded && (
                      <Typography variant="caption" color="success.dark" sx={{ mt: 0.5, display: "block" }}>
                        {categoriaSeleccionada
                          ? `${categoriaSeleccionada.nombre} — ${
                              categoriaSeleccionada.tipo_calculo === "fixed"
                                ? `$${Number(categoriaSeleccionada.precio_fijo || 0).toFixed(2)} fijo`
                                : categoriaSeleccionada.tipo_calculo === "personalizada"
                                  ? "monto libre"
                                  : categoriaSeleccionada.precio_por_hora
                                    ? `$${Number(categoriaSeleccionada.precio_por_hora).toFixed(2)}/h`
                                    : `${categoriaSeleccionada.minutos_gratis} min gratis`
                            }`
                          : "Tarifa por defecto"}
                      </Typography>
                    )}
                  </CardContent>
                  <Collapse in={categoriaExpanded}>
                    <CardContent sx={{ bgcolor: "#f1f8e9", pt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                        Al elegir una categoría, el total se recalcula con su tarifa.
                      </Typography>
                      <FormControl fullWidth size="small">
                        <InputLabel>Categoría de cobro</InputLabel>
                        <Select
                          label="Categoría de cobro"
                          value={categoriaSeleccionada?.id ?? ""}
                          onChange={(e) => {
                            const id = e.target.value;
                            setCategoriaSeleccionada(
                              id === "" ? null : categorias.find((c) => c.id === id) || null
                            );
                          }}
                        >
                          <MenuItem value="">
                            Tarifa por defecto del parqueadero
                          </MenuItem>
                          {categorias.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                              {cat.nombre}
                              {cat.tipo_calculo === "fixed"
                                ? ` — $${Number(cat.precio_fijo || 0).toFixed(2)} fijo`
                                : cat.tipo_calculo === "personalizada"
                                  ? " — monto libre"
                                  : cat.precio_por_hora
                                    ? ` — $${Number(cat.precio_por_hora).toFixed(2)}/h`
                                    : ` (${cat.minutos_gratis} min)`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {esPersonalizada && (
                        <TextField
                          fullWidth size="small" type="number" sx={{ mt: 1.5 }}
                          label="Monto a cobrar ($)"
                          value={montoPersonalizado}
                          onChange={(e) => setMontoPersonalizado(e.target.value)}
                          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                          helperText="Categoría personalizada: el monto lo defines tú"
                        />
                      )}
                    </CardContent>
                  </Collapse>
                </Card>
              )}

              <Card
                variant="outlined"
                sx={{
                  bgcolor: esGratisConDescuento() ? "#e8f5e9" : "#fff3e0",
                }}
              >
                <CardContent>
                  <FormControlLabel
                    sx={{ mb: 1 }}
                    control={
                      <Checkbox
                        checked={ticketPerdido}
                        onChange={(e) => setTicketPerdido(e.target.checked)}
                        color="warning"
                      />
                    }
                    label={`Ticket perdido (+$${Number(config.lostTicketValue || 0).toFixed(2)})`}
                  />

                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <AttachMoney color="primary" />
                    <Typography variant="h6" color="primary">
                      Detalle del Cobro
                    </Typography>
                  </Box>

                  {categoriasVehiculo.length > 0 && (
                    <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={cambiandoTipoVehiculo || loading}>
                      <InputLabel>Tipo de vehículo</InputLabel>
                      <Select
                        value={tipoVehiculoActual || ""}
                        label="Tipo de vehículo"
                        onChange={(e) => handleCambiarTipoVehiculo(e.target.value)}
                      >
                        {categoriasVehiculo.map((c) => (
                          <MenuItem key={c.slug} value={c.slug}>
                            {c.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: "block" }}>
                        {cambiandoTipoVehiculo
                          ? "Actualizando tarifa..."
                          : "Corrige el tipo si la cámara lo detectó mal — la tarifa se recalcula automáticamente"}
                      </Typography>
                    </FormControl>
                  )}

                  {esGratisConDescuento() ? (
                    <Box sx={{ textAlign: "center", py: 2 }}>
                      <CheckCircle
                        sx={{ fontSize: 60, color: "success.main", mb: 1 }}
                      />
                      <Typography variant="h5" color="success.main">
                        Sin Cobro
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {cobroInfo.es_gratis
                          ? "El vehículo estuvo dentro del tiempo gratis"
                          : "Las horas descontadas cubren el total del parqueo"}
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">
                            Horas Iniciales
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {cobroInfo.horas_cobradas} hrs
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">
                            {typePriceCalcEfectivo === "fixed"
                              ? "Valor Fijo"
                              : "Precio por Hora"}
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            ${
                              typePriceCalcEfectivo === "fixed"
                                ? Number(fixedPriceEfectivo || 0).toFixed(2)
                                : precioPorHoraEfectivo.toFixed(2)
                            }
                            {categoriaSeleccionada?.precio_por_hora != null && typePriceCalcEfectivo !== "fixed" ? (
                              <Typography variant="caption" color="warning.main" display="block">
                                tarifa {categoriaSeleccionada.nombre}
                              </Typography>
                            ) : cobroInfo?.tarifa_aplicada?.origen === "grupo" && (
                              <Typography variant="caption" color="warning.main" display="block">
                                tarifa {cobroInfo.tarifa_aplicada.grupo_nombre} ({cobroInfo?.vehiculo?.tipo_label})
                              </Typography>
                            )}
                          </Typography>
                        </Grid>
                        {facturas.length > 0 && typePriceCalcEfectivo !== "fixed" && (
                          <>
                            <Grid item xs={12}>
                              <Divider />
                            </Grid>
                            <Grid item xs={6}>
                              <Typography
                                variant="body2"
                                color="textSecondary"
                              >
                                Horas Descontadas
                              </Typography>
                              <Typography
                                variant="h6"
                                fontWeight="bold"
                                color="warning.main"
                              >
                                - {calcularHorasDescontadas()} hrs
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography
                                variant="body2"
                                color="textSecondary"
                              >
                                Horas a Cobrar
                              </Typography>
                              <Typography
                                variant="h6"
                                fontWeight="bold"
                                color="success.main"
                              >
                                {calcularHorasEfectivas()} hrs
                              </Typography>
                            </Grid>
                          </>
                        )}
                        {ticketPerdido && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="warning.dark">
                              Recargo ticket perdido: + ${calcularCargoTicketPerdido().toFixed(2)}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>

                      <Divider sx={{ my: 2 }} />

                      <Box
                        sx={{
                          bgcolor: "#393939",
                          color: "white",
                          p: 3,
                          borderRadius: 2,
                          textAlign: "center",
                        }}
                      >
                        {facturas.length > 0 && (
                          <>
                            <Typography
                              variant="body2"
                              sx={{
                                textDecoration: "line-through",
                                opacity: 0.7,
                              }}
                            >
                              ${cobroInfo.total_a_pagar.toFixed(2)}
                            </Typography>
                          </>
                        )}
                        <Typography
                          variant="h4"
                          fontWeight="bold"
                          style={{ color: "white" }}
                        >
                          ${totalMostrado().toFixed(2)}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Total a Pagar
                        </Typography>
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            !loading && (
              <Alert severity="warning">
                No se pudo obtener la información del cobro
              </Alert>
            )
          )}
        </Grid>
      </Grid>
    )}
  </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} color="inherit" disabled={procesandoPago}>
          Cancelar
        </Button>
        {cobroInfo && !esGratisConDescuento() && (
          <Button
            onClick={handleConfirmarPago}
            variant="contained"
            startIcon={<CheckCircle />}
            disabled={procesandoPago}
            sx={{
              background: "#393939ff",
              "&:hover": { background: "#2a2a2a" },
            }}
          >
            {procesandoPago ? "Procesando..." : "Confirmar Pago"}
          </Button>
        )}
        {cobroInfo && esGratisConDescuento() && (
          <Button
            onClick={handleConfirmarPago}
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
            disabled={procesandoPago}
          >
            Permitir Salida
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDialog;
